/* The share dialog.
   =================

   Who can see a document: nobody, named colleagues, the whole team, or whoever
   covers an attached client. It writes exactly the four fields the Firestore
   rule for /notes reads, together, so they cannot drift apart.

   Moved out of app.html unchanged apart from being wrapped in onReady(). See
   ready.js for why that wrapper is not optional: the original opened with
   `if (!dlg) return;`, which in the bundle would fire on every load and turn
   sharing off without a single line in the console.
*/

import { onReady } from '../lib/ready.js';

export function initShareDialog() {
  const $ = id => document.getElementById(id);
  const dlg = $('sh-dlg');
  if (!dlg) return;   // now only true if the markup is genuinely absent

  const esc = v => String(v == null ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  let st = null;   // { note, scope, people:Set, clients:[{id,name}] }

  const noteById = id => {
    try {
      if (typeof notesVisible === 'function') return notesVisible().find(n => n.id === id) || null;
    } catch (e) {}
    return null;
  };

  function open(noteId) {
    const note = noteById(noteId);
    if (!note) { alert('Open a document first.'); return; }
    let people = [];
    try { people = (typeof notesGetShared === 'function') ? notesGetShared(note) : []; } catch (e) {}
    let clients = [];
    try { clients = JSON.parse(note.clientsJson || '[]') || []; } catch (e) {}
    st = {
      note,
      scope: note.shareAll ? 'team' : (people.length ? 'people' : 'private'),
      people: new Set(people),
      clients
    };
    render();
    dlg.hidden = false;
  }
  function close() { dlg.hidden = true; st = null; }

  function render() {
    if (!st) return;
    document.querySelectorAll('#sh-scope button').forEach(b =>
      b.classList.toggle('on', b.dataset.v === st.scope));
    $('sh-people-fld').hidden = st.scope !== 'people';

    const me = (typeof currentUser !== 'undefined' && currentUser) ? currentUser.uid : null;
    const members = (typeof teamMembersCache !== 'undefined' ? teamMembersCache : [])
      .filter(m => m.uid !== me);
    $('sh-people').innerHTML = members.length ? members.map(m => `
      <label class="sh-row">
        <input type="checkbox" data-uid="${esc(m.uid)}"${st.people.has(m.uid) ? ' checked' : ''}>
        <span>${esc(m.displayName || m.name || m.email || 'Member')}
          ${m.role && m.role !== 'member' ? `<small> · ${esc(m.role)}</small>` : ''}</span>
      </label>`).join('') : '<div class="sh-empty">Nobody else is on this team yet.</div>';
    $('sh-people').querySelectorAll('[data-uid]').forEach(b =>
      b.addEventListener('change', () => {
        if (b.checked) st.people.add(b.dataset.uid); else st.people.delete(b.dataset.uid);
      }));

    $('sh-chips').innerHTML = st.clients.map((c, i) =>
      `<span class="sh-chip">${esc(c.name)}<button type="button" data-rm="${i}">&times;</button></span>`).join('');
    $('sh-chips').querySelectorAll('[data-rm]').forEach(b =>
      b.addEventListener('click', () => { st.clients.splice(+b.dataset.rm, 1); render(); }));

    renderClientList($('sh-client-search').value || '');
  }

  function renderClientList(q) {
    const pool = (typeof mileageClientPool === 'function') ? mileageClientPool() : [];
    const term = q.trim().toLowerCase();
    const taken = new Set(st.clients.map(c => c.id));
    const list = pool
      .filter(c => !taken.has(c.id))
      .filter(c => !term || (c.name || '').toLowerCase().includes(term))
      .slice(0, 40);
    $('sh-clients').innerHTML = list.length
      ? list.map(c => `<div class="sh-row" data-cid="${esc(c.id)}">${esc(c.name || 'Unnamed')}</div>`).join('')
      : `<div class="sh-empty">${term ? 'Nobody matches that.' : 'No clients assigned to you.'}</div>`;
    $('sh-clients').querySelectorAll('[data-cid]').forEach(row =>
      row.addEventListener('click', () => {
        const c = pool.find(x => x.id === row.dataset.cid);
        if (c) { st.clients.push({ id: c.id, name: c.name }); render(); }
      }));
  }

  async function save() {
    if (!st) return;
    const clientsJson = JSON.stringify(st.clients);
    /* Every field the Firestore rule for /notes actually reads, written
       together. clientAssignedUids is what lets a colleague covering the same
       client open this — it is derived, never typed, so it cannot drift out of
       step with the attached list. */
    const patch = {
      shareAll: st.scope === 'team',
      sharedWithJson: JSON.stringify(st.scope === 'people' ? [...st.people] : []),
      clientsJson,
      clientAssignedUids: (typeof notesClientAccessUids === 'function')
        ? notesClientAccessUids(clientsJson) : []
    };
    try {
      if (typeof notesTeamMode === 'function' && notesTeamMode()) {
        if (typeof notesSyncShareArray === 'function') notesSyncShareArray(patch);
        await fbDb.collection('teams').doc(activeTeamId).collection('notes').doc(st.note.id)
          .update({ ...patch, updatedAt: new Date().toISOString() });
        const c = (typeof teamNotesCache !== 'undefined')
          ? teamNotesCache.find(n => n.id === st.note.id) : null;
        if (c) Object.assign(c, patch);
      } else {
        const data = notesLoadPersonal();
        const n = data.notes.find(x => x.id === st.note.id);
        if (n) Object.assign(n, patch);
        notesSavePersonal(data);
      }
      close();
      if (typeof renderNotes === 'function') renderNotes();
    } catch (e) {
      console.warn('share save failed:', e && (e.code || e.message));
      alert('Could not save sharing: ' + ((e && e.message) || e));
    }
  }

  document.querySelectorAll('#sh-scope button').forEach(b =>
    b.addEventListener('click', () => { st.scope = b.dataset.v; render(); }));
  $('sh-client-search').addEventListener('input', e => renderClientList(e.target.value));
  $('sh-cancel').addEventListener('click', close);
  $('sh-ok').addEventListener('click', save);
  dlg.addEventListener('click', e => { if (e.target === dlg) close(); });
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && !dlg.hidden) close(); });

  /* Delegated: the three editors' toolbars are built further down the file, so
     binding directly would attach to nothing. Same trap as the create dialog. */
  document.addEventListener('click', e => {
    const btn = e.target.closest && e.target.closest('[data-shareopen]');
    if (!btn) return;
    if (typeof notesActiveId !== 'undefined' && notesActiveId) open(notesActiveId);
    else alert('Open a document first.');
  });
}

onReady(initShareDialog);
